<script setup>
import { ref, watch, computed  } from 'vue';
import { useOrderStore } from '@/stores/OrderStore';
import { useAuthStore } from '@/stores/authStore';

// Get order store
const orderStore = useOrderStore();
const authStore = useAuthStore();

const activeTab = ref('receipts'); 

// function showSavedReceipts (receipt, index) {
//   // orderStore.loadSavedReceipts();
//   // savedReceipts.value = orderStore.savedReceipts;
//   orderStore.showDraftList = !orderStore.showDraftList
//   // orderStore.toggleDraftList;
//   console.log('List Clicked', receipt);
//   authStore.toggleCheckoutPopup();
// }

defineProps({
  draftCount: Number, 
});

// In the notification component script
function selectReceipt(receipt, index) {
  // Pass the receipt data to the order store
  orderStore.setReceiptForReprint(receipt);
  
  // Close the notification popup
  orderStore.toggleDraftList();
  
  // Open the checkout popup
  authStore.toggleCheckoutPopup();
  
  // Set the checkout as successful to show the invoice
  // isCheckoutSuccessful.value = true;
  // isInvoiceVisible.value = true;
}

// Function to load a draft order
function loadDraftOrder(draft) {
  const draftIndex = orderStore.draftOrders.indexOf(draft);
  if (draftIndex !== -1) {
    orderStore.loadDraftOrder(draftIndex);
    orderStore.toggleDraftList();
  }
}

// Helper function to calculate the total number of items in a draft
function getTotalItems(draft) {
  return draft.orderItems.reduce((total, item) => total + item.Qty, 0); 
}

// Helper function to calculate the number of unique SKUs in a draft
function getUniqueSKUs(draft) {
  const uniqueSkus = new Set(draft.orderItems.map(item => item.Sku));
  return uniqueSkus.size;
}

// Function to delete a draft order from the store and localStorage
function deleteDraftOrder(index) {
  orderStore.removeDraftOrder(index);
}

const savedReceipts = ref([]);

const formatReceiptDate = (timestamp) => {
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Filters
const priceInput = ref("");
const skuInput = ref("");

const activePriceFilter = ref("");
const activeSkuFilter = ref("");

const hasReceipts = ref(false);

// watch(() => orderStore.isSavedReceipt, () => {
//   hasReceipts.value = orderStore.isSavedReceipt;
// }, { immediate: true });

const visibleReceipts = ref([]); 
const receiptsPerPage = 20;
const currentPage = ref(1);

function loadMore() {
  const start = currentPage.value * receiptsPerPage;
  const end = start + receiptsPerPage;
  const nextReceipts = filteredReceipts.value.slice(start, end);
  if (nextReceipts.length > 0) {
    visibleReceipts.value.push(...nextReceipts);
    currentPage.value++;
  }
}

// Filtered receipts
const filteredReceipts = ref([]);

// Watch saved receipts and update filtered list
watch(
  () => orderStore.isSavedReceipt,
  async () => {
    hasReceipts.value = orderStore.isSavedReceipt;
    if (hasReceipts.value) {
      savedReceipts.value = orderStore.getSavedReceipts()
        .map((receipt) => ({
          ...receipt,
          date:
            typeof receipt.date === "string"
              ? new Date(receipt.date).getTime()
              : receipt.date,
        }))
        .sort((a, b) => b.date - a.date);

      applyFilters();
    }
  },
  { immediate: true }
);

// Apply filters
// based on greater than or equal for price
// and includes for SKU
  // Reset when filters applied
function applyFilters() {
  filteredReceipts.value = savedReceipts.value.filter((receipt) => {
    let matches = true;
    if (activePriceFilter.value) {
      matches = matches && Number(receipt.totalAmount) >= Number(activePriceFilter.value);
    }
    if (activeSkuFilter.value) {
      const hasSku = receipt.items?.some(
        (item) =>
          String(item.sku).includes(activeSkuFilter.value) ||
          String(item.Barcode).includes(activeSkuFilter.value)
      );
      matches = matches && hasSku;
    }
    return matches;
  });

  currentPage.value = 1;
  visibleReceipts.value = filteredReceipts.value.slice(0, receiptsPerPage); // 👈 reset
}

// handle Enter key
function handlePriceEnter(e) {
  if (e.key === "Enter") {
    activePriceFilter.value = priceInput.value;
    applyFilters();
  }
}
function handleSkuEnter(e) {
  if (e.key === "Enter") {
    activeSkuFilter.value = skuInput.value;
    applyFilters();
  }
}

// Clear filters
function clearPriceFilter() {
  activePriceFilter.value = "";
  priceInput.value = "";
  applyFilters();
}
function clearSkuFilter() {
  activeSkuFilter.value = "";
  skuInput.value = "";
  applyFilters();
}
// handle infinite scrole
function onScroll(e) {
  const target = e.target;
  if (
    target.scrollTop + target.clientHeight >= target.scrollHeight - 10 &&
    visibleReceipts.value.length < filteredReceipts.value.length
  ) {
    loadMore();
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="modal-outer">
      <div v-show="orderStore.showDraftList" @click="orderStore.toggleDraftList"
        class="absolute w-full bg-black bg-opacity-30 h-screen top-0 left-0 flex justify-center px-8 z-10">
        <Transition name="modal-inner">
          <div v-if="(orderStore.draftOrders && orderStore.draftOrders.length) || hasReceipts" @click.stop
            class="flex  p-4 my-10 w-10/12 bg-white self-start rounded-2xl">
            
            <div class="w-1/2">
              <h1 class="text-xl">Notifications</h1>
            </div>
            <div class="w-1/2">
              <!-- Tab Navigation -->
            <div class="flex border-b border-gray-200">
              <button 
                @click="activeTab = 'receipts'"
                :class="{
                  'border-b-2 border-purple-500 text-purple-600': activeTab === 'receipts',
                  'text-gray-500 hover:text-gray-700': activeTab !== 'receipts'
                }"
                class="py-2 px-4 font-medium focus:outline-none text-xl"
                :disabled="!hasReceipts">
                Saved Receipts
              </button>
              
              <button 
                @click="activeTab = 'drafts'"
                :class="{
                  'border-b-2 border-purple-500 text-purple-600': activeTab === 'drafts',
                  'text-gray-500 hover:text-gray-700': activeTab !== 'drafts'
                }"
                class="relative py-2 px-4 font-medium focus:outline-none text-xl">
                Orders On Hold
                <span v-if="draftCount > 0 && ( authStore.isUserLoggedIn || authStore.isManagerLoggedIn)"
                  class="absolute top-0 right-3 bg-purple-500 text-white text-xs font-bold px-2 py-1 rounded-full"
                  style="transform: translate(50%, -50%);"
                >
                  {{ draftCount }}
                </span>
              </button>
            </div>
            
            <!-- Tab Content -->
            <div class="flex-1 overflow-hidden">
              <!-- Saved Receipts Tab -->
              <div v-show="activeTab === 'receipts'" class="h-full overflow-y-auto" @scroll="onScroll">
                <div v-if="hasReceipts" class="mt-4">

                  <div v-if="authStore.isManagerLoggedIn" class="flex gap-4 items-center mb-4">
                    <!-- totalPrice filter -->
                    <div class="flex items-center gap-2 relative">
                      <i
                        class="pi pi-filter p-1 rounded"
                        :class="activePriceFilter ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-600'"
                      ></i>
                      <input
                        type="number"
                        v-model="priceInput"
                        placeholder="Filter by $total"
                        class="border border-gray-300 rounded p-1 pr-6"
                        :disabled="!!activePriceFilter"
                        @keyup.enter="handlePriceEnter"
                      />
                      <button
                        v-if="activePriceFilter"
                        @click="clearPriceFilter"
                        class="absolute right-2 text-gray-400 hover:text-gray-700"
                      >
                        ✕
                      </button>
                    </div>
                  
                    <!-- retailSKU filter -->
                    <div class="flex items-center gap-2 relative">
                      <i
                        class="pi pi-filter p-1 rounded"
                        :class="activeSkuFilter ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-600'"
                      ></i>
                      <input
                        type="text"
                        v-model="skuInput"
                        placeholder="Filter by retailSku"
                        class="border border-gray-300 rounded p-1 pr-6"
                        :disabled="!!activeSkuFilter"
                        @keyup.enter="handleSkuEnter"
                      />
                      <button
                        v-if="activeSkuFilter"
                        @click="clearSkuFilter"
                        class="absolute right-2 text-gray-400 hover:text-gray-700"
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  <ul 
                    class="space-y-2 max-h-[720px] overflow-y-auto"
                    @scroll="onScroll"
                  >
                    <li
                      v-for="(receipt, index) in visibleReceipts"
                      :key="index"
                      class="p-2 hover:bg-gray-100 rounded cursor-pointer border-b flex items-center gap-3"
                      @click="selectReceipt(receipt, index)"
                    >
                      <img
                        :src="receipt.previewImage"
                        class="w-12 h-12 object-cover rounded-lg border"
                        :alt="`Product for ${receipt.id}`"
                      />
                      <div class="flex-1 min-w-0">
                        <div class="flex justify-between items-center">
                          <span class="font-medium truncate">
                            Invoice #{{ receipt.invoiceId }}
                          </span>
                          <span class="text-sm text-gray-500">
                            {{ formatReceiptDate(receipt.date) }}
                          </span>
                        </div>
                        <div class="flex justify-between text-sm mt-1">
                          <span class="text-gray-500 truncate"></span>
                          <span class="font-medium">
                            Total: ${{ receipt.totalAmount || "0.00" }}
                          </span>
                        </div>
                      </div>
                    </li>
                  </ul>

                  <div v-if="visibleReceipts.length < filteredReceipts.length" class="text-center p-2 text-gray-400">
                    Scroll to load more...
                  </div>
                  
                  <div v-else-if="filteredReceipts.length > 0" class="text-center p-2 text-gray-400">
                    All receipts loaded
                  </div>

                </div>


                  
                  
                <div v-else class="p-4 text-center text-gray-500">
                  No saved receipts available
                </div>
              </div>
              
              <!-- Draft Orders Tab -->
              <div v-show="activeTab === 'drafts'" class="h-full overflow-y-auto">
                <div v-if="orderStore.draftOrders?.length" class="mt-4">
                  <ul class="grid grid-cols-4 gap-2 relative">
                    <li v-for="(draft, index) in orderStore.draftOrders" :key="index"
                      class="collapse rounded-2xl bg-[#f4f5f7] mb-2 mx-2">
                      <i @click="deleteDraftOrder(index)"
                        class="pi pi-trash absolute top-3 right-3 text-3xl cursor-pointer z-50"></i>
                      <div class="collapse-title text-xl font-medium flex justify-center items-center mx-auto">
                        <div class="flex flex-col items-center justify-center gap-4">
                          <img 
                            v-if="draft.orderItems && draft.orderItems[0]?.ItemImage" 
                            @click="loadDraftOrder(draft)"
                            :src="`${draft.orderItems[0]?.ItemImage || 'placeholder.png'}`" 
                            class="rounded-lg"
                            alt="product-img" />
                          <div class="flex gap-2">
                            <p @click="loadDraftOrder(draft)" class="text-sm font-semibold">{{ getTotalItems(draft) }} items</p>
                            <p @click="loadDraftOrder(draft)" class="text-sm font-semibold">{{ getUniqueSKUs(draft)}} SKUs</p>
                          </div>
                          <p @click="loadDraftOrder(draft)" class="text-base font-medium">{{ orderStore.formatDate(draft.timestamp) }}</p>
                        </div>
                      </div>
                      <div class="collapse-content">
                        <ul class="ml-6">
                          <li v-for="(item, itemIndex) in draft.orderItems" :key="itemIndex"
                            class="flex justify-between items-center mb-2">
                            <div class="flex items-center gap-4">
                              <img v-if="item.ImageUrl"
                                :src="`${item.ImageUrl}`"
                                class="w-10 rounded-lg" alt="product-img" />
                              <div>
                                <p class="text-base font-semibold">{{ item.ItemName }}</p>
                                <p class="text-sm text-gray-600">Qty: {{ item.Qty }}</p>
                              </div>
                            </div>
                            <p class="font-semibold">${{ (item.Price * item.Qty).toFixed(2) }}</p>
                          </li>
                        </ul>
                      </div>
                    </li>
                  </ul>
                </div>
                <div v-else class="p-4 text-center text-gray-500">
                  No draft orders available
                </div>
              </div>
            </div>
            </div>
          </div>
          <p v-else class="bg-white my-10 h-20 px-20 flex justify-center items-center rounded-2xl font-semibold">
            No available info
          </p>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>


<style scoped>
.modal-outer-enter-active,
  .modal-outer-leave-active {
    transition: opacity 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
  }

  .modal-outer-enter-from,
  .modal-outer-leave-to {
    opacity: 0;
  }

  .modal-inner-enter-active {
    transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12) 0.15s;
  }
  
  .modal-inner-leave-active {
    transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.12);
  }

  .modal-inner-enter-from {
    opacity: 0;
    transform: scale(0.8);
  }

  .modal-inner-leave-to {
    transform: scale(0.8);
  }

  .custom-input::placeholder {
    color: gray;
  }

  /* For saved recipts */
  li:nth-child(odd) {
    background-color: #f3f4f6;
  }

  li:nth-child(even) {
    background-color: white;
  }

  li:hover {
    background-color: #e5e7eb !important;
  }
</style>
